import { useState } from 'react';
import {
  CharliePopover,
  CharliePopoverTrigger,
  CharliePopoverContent,
  CharlieCommand,
  CharlieCommandInput,
  CharlieCommandList,
  CharlieCommandEmpty,
  CharlieCommandGroup,
  CharlieCommandItem,
} from '@ver.id/libs.ui.charlie';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { type Locale, useTranslation } from '../i18n/i18n';

/**
 * Flag emoji per locale. Using emoji flags for simplicity;
 * could be replaced with SVG flag components for pixel-perfect rendering.
 */
const FLAGS: Record<Locale, string> = {
  en: '\u{1F1EC}\u{1F1E7}',
  nl: '\u{1F1F3}\u{1F1F1}',
  de: '\u{1F1E9}\u{1F1EA}',
  fr: '\u{1F1EB}\u{1F1F7}',
  sv: '\u{1F1F8}\u{1F1EA}',
  es: '\u{1F1EA}\u{1F1F8}',
};

const LOCALES: Locale[] = ['en', 'nl', 'de', 'fr', 'sv', 'es'];

type LanguageSelectorProps = {
  /** Compact mode: show only the flag icon (used in the mobile top bar). */
  compact?: boolean;
};

/**
 * LanguageSelector — Combobox/command menu for switching locales.
 *
 * Figma: Top-right in all desktop frames; compact flag button in mobile top bar.
 * Combobox overlay: https://www.figma.com/design/6V1D3oNy17pfFNHIRb2nmm/Ronan-V2---UX?node-id=300-19168
 *
 * Desktop trigger: flag + language name + chevron in a bordered card.
 * Mobile trigger (compact): flag-only bordered square button.
 * Popover: search input + flag/name list with accent highlight on selected.
 */
export default function LanguageSelector({ compact }: LanguageSelectorProps) {
  const { locale, setLocale, t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <CharliePopover open={open} onOpenChange={setOpen}>
      <CharliePopoverTrigger asChild>
        {compact ? (
          <button
            type="button"
            className={`flex items-center justify-center size-10 rounded-md border bg-muted/50 overflow-hidden ${
              open ? 'border-ring ring-3 ring-ring/20' : 'border-input'
            }`}
            aria-label={t('topBar.language')}
          >
            <span className="text-base leading-none">{FLAGS[locale]}</span>
          </button>
        ) : (
          <button
            type="button"
            className="flex h-9 items-center gap-2 rounded-md border border-input bg-card px-3 py-2 shadow-xs"
            aria-label={t('topBar.language')}
          >
            <span className="text-base leading-none">{FLAGS[locale]}</span>
            <span className="text-sm text-muted-foreground">
              {t(`language.${locale}`)}
            </span>
            <FontAwesomeIcon
              icon={faChevronDown}
              className={`size-4 shrink-0 text-muted-foreground transition-transform ${
                open ? 'rotate-180' : ''
              }`}
            />
          </button>
        )}
      </CharliePopoverTrigger>
      <CharliePopoverContent className="w-64 p-0" align="end">
        <CharlieCommand>
          <CharlieCommandInput placeholder={t('language.search')} />
          <CharlieCommandList>
            <CharlieCommandEmpty>{t('language.noResults')}</CharlieCommandEmpty>
            <CharlieCommandGroup>
              {LOCALES.map((loc) => (
                <CharlieCommandItem
                  key={loc}
                  value={t(`language.${loc}`)}
                  onSelect={() => {
                    setLocale(loc);
                    setOpen(false);
                  }}
                  className={
                    loc === locale ? 'bg-accent text-accent-foreground' : ''
                  }
                >
                  <span className="text-base leading-none shrink-0">
                    {FLAGS[loc]}
                  </span>
                  <span className="flex-1 truncate">
                    {t(`language.${loc}`)}
                  </span>
                </CharlieCommandItem>
              ))}
            </CharlieCommandGroup>
          </CharlieCommandList>
        </CharlieCommand>
      </CharliePopoverContent>
    </CharliePopover>
  );
}
